/**
 * @file SkeletonEvidenceCard.tsx
 *
 * @description A loading placeholder component that mimics the appearance of an evidence card.
 * This component is displayed during data fetching operations to improve perceived
 * performance and provide visual feedback to law enforcement users.
 *
 * @module Components/Skeletons
 * @requires Material-UI
 * @requires React
 */

import React from "react";
import { Card, CardContent, Box, Stack, Skeleton } from "@mui/material";
import Grid from "@mui/material/Grid";

/**
 * @function SkeletonEvidenceCard
 *
 * @description A placeholder component that mimics the layout and structure of an evidence card
 * using Skeleton animations. This component should be shown while the actual evidence
 * data is loading to provide visual feedback to police officers using the application.
 *
 * The component follows the same structure as the actual EvidenceCard
 * to prevent layout shifts when the real content loads.
 *
 * @returns {JSX.Element} A skeleton loading representation of an evidence card
 */
const SkeletonEvidenceCard: React.FC = () => {
  return (
    <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 3 }}>
      <Card
        sx={{
          height: "100%",
          width: "100%",
          minWidth: { xs: "200px", xxl: "240px", uhd: "280px" },
          maxWidth: { xs: "300px", xxl: "360px", uhd: "420px" },
          margin: "auto",
          borderRadius: "12px",
          backgroundColor: "background.paper",
          borderColor: "divider",
          borderWidth: 1,
          borderStyle: "solid",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        {/* Thumbnail / media area with overlaid status Chip placeholder */}
        <Box sx={{ position: "relative" }}>
          <Skeleton
            variant="rectangular"
            width="100%"
            animation="wave"
            sx={{
              height: { xs: 140, xxl: 170, uhd: 200 },
              borderRadius: "12px 12px 0 0",
            }}
          />
          <Skeleton
            variant="rounded"
            width={64}
            height={24}
            animation="wave"
            sx={{ position: "absolute", top: 8, right: 8 }}
          />
        </Box>

        <CardContent sx={{ p: 2, flexGrow: 1 }}>
          <Stack spacing={1.5} mt={1}>
            {/* Title row: icon + filename */}
            <Box display="flex" alignItems="center" gap={1.5}>
              <Skeleton variant="circular" width={16} height={16} animation="wave" />
              <Skeleton variant="text" width="80%" height={20} animation="wave" />
            </Box>

            {/* Metadata row: icon + date */}
            <Box display="flex" alignItems="center" gap={1.5}>
              <Skeleton variant="circular" width={16} height={16} animation="wave" />
              <Skeleton variant="text" width="65%" height={20} animation="wave" />
            </Box>

            {/* Metadata row: icon + device */}
            <Box display="flex" alignItems="center" gap={1.5}>
              <Skeleton variant="circular" width={16} height={16} animation="wave" />
              <Skeleton variant="text" width="55%" height={20} animation="wave" />
            </Box>
          </Stack>
        </CardContent>

        {/* Action button + uploaded caption */}
        <Box sx={{ p: 2, pt: 0 }}>
          <Skeleton
            variant="rounded"
            width="100%"
            height={40}
            animation="wave"
            sx={{ mb: 1.5 }}
          />
          <Skeleton
            variant="text"
            width="60%"
            height={18}
            animation="wave"
            sx={{ mx: "auto" }}
          />
        </Box>
      </Card>
    </Grid>
  );
};

export default SkeletonEvidenceCard;
